'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Globe, Hash } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Segmented, StatusPill, Tag, LoadingState, EmptyState } from '../ui'
import { BaseDashboard, baseDashboardClasses } from '../BaseDashboard'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { useListReturn, LAST_OPENED_ROW_CLASS } from './useListReturn'
import { apiGet, useCatalog } from './entriesApi'
import { CampaignDetailPage } from './CampaignDetailPage'
import { SearchableSelect } from './SearchableSelect'
import type { CampaignSummary } from './campaignTypes'
import { isMultiIdentifierCampaign } from './campaignTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'

// Campaigns are fully derived — no campaigns table, no curation, no "accept"
// step anywhere (see getCampaignsFlat's own comment in
// backend/queries/postManagement.ts). A campaign is a connected component of
// the (entry <-> domain/entity_reference) graph, computed fresh on every
// request by unified_entries_flat. campaignId is therefore NOT a stable key
// — never held onto across a refetch, only ever used within the lifetime of
// one list/detail load (see campaignTypes.ts's header comment).

const CAMPAIGN_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'domain', label: 'Domain' },
  { value: 'multiple_domain', label: 'Multiple domains' },
  { value: 'entity_reference', label: 'Entity reference' },
  { value: 'multiple_entity_reference', label: 'Multiple entity references' },
]

function fmtDate(iso?: string) {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
  return `${time} ${date}`
}

// Short, readable summary of a campaign's identifier set for the list row —
// the detail page shows the full set; this just needs enough to recognize
// the campaign at a glance. Domains lead (their url is inherently more
// recognizable than an entity+reference pair) and each list caps at 2 with
// a "+N" overflow rather than wrapping the row across multiple lines.
function identifierSummary(c: CampaignSummary): { icon: 'domain' | 'entity'; text: string }[] {
  const parts: { icon: 'domain' | 'entity'; text: string }[] = []
  const domainShown = c.domains.slice(0, 2)
  for (const d of domainShown) parts.push({ icon: 'domain', text: d.domain_url })
  if (c.domains.length > domainShown.length) parts.push({ icon: 'domain', text: `+${c.domains.length - domainShown.length} more` })
  const entityShown = c.entityReferences.slice(0, 2)
  for (const e of entityShown) parts.push({ icon: 'entity', text: `${e.entity} / ${e.reference}` })
  if (c.entityReferences.length > entityShown.length) parts.push({ icon: 'entity', text: `+${c.entityReferences.length - entityShown.length} more` })
  return parts
}

export function CampaignsDashboardView({
  theme,
  platformIds,
  storageKey,
  isEditor,
}: {
  theme: ProcessedTheme
  platformIds?: number[]
  storageKey: string
  isEditor?: boolean
}) {
  const { muted, border } = baseDashboardClasses(theme)
  const { catalog } = useCatalog()

  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [scanCapped, setScanCapped] = useState(false)
  // Server-paged at PAGE_SIZE (20) by /campaign-groups — same append-on-
  // "Load more" model as CasesDashboardView / UnifiedEntriesDashboardView.
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
  // Coming back from a campaign: same scroll position, the row highlighted.
  const back = useListReturn()

  const [sortBy, setSortBy] = useState<'recent' | 'entries'>('recent')
  const [onlyMulti, setOnlyMulti] = useState(false)
  const [campaignType, setCampaignType] = useState('')
  // Interactive platform filter — separate from the `platformIds` prop
  // (which pre-scopes a whole brand-specific instance of this view, the way
  // UnifiedEntriesDashboardView's own platformIds prop does for per-brand
  // callers). Picking one here narrows further; no pick falls back to
  // whatever the prop already scoped, or unscoped if there's no prop either.
  const [platformFilter, setPlatformFilter] = useState<number | ''>('')

  const effectivePlatformIds = platformFilter !== '' ? [platformFilter] : platformIds
  const platformIdsKey = effectivePlatformIds && effectivePlatformIds.length > 0 ? effectivePlatformIds.join(',') : ''

  const fetchCampaigns = useCallback(async (targetPage = 0, isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else if (targetPage === 0) setLoading(true)
    else setLoadingMore(true)
    setError(null)
    const params = new URLSearchParams({ page: String(targetPage), sort: sortBy })
    if (onlyMulti) params.set('only_multi', '1')
    if (campaignType) params.set('campaign_type', campaignType)
    if (platformIdsKey) params.set('platform_ids', platformIdsKey)
    const result = await apiGet<{ campaigns: CampaignSummary[]; total: number; hasMore: boolean; scanCapped: boolean }>(
      `/campaign-groups?${params.toString()}`,
    )
    if (result.success && result.data) {
      setCampaigns((prev) => (targetPage === 0 ? result.data!.campaigns : [...prev, ...result.data!.campaigns]))
      setTotal(result.data.total)
      setScanCapped(result.data.scanCapped)
      setHasMore(result.data.hasMore)
      setPage(targetPage)
    } else {
      setError(result.error ?? 'Failed to load campaigns')
    }
    setLoading(false)
    setRefreshing(false)
    setLoadingMore(false)
  }, [sortBy, onlyMulti, campaignType, platformIdsKey])

  useEffect(() => { fetchCampaigns() }, [fetchCampaigns])

  const columns: ColumnDef[] = useMemo(() => [
    { key: 'type', label: 'Type', initialWidth: 160 },
    { key: 'entries', label: 'Entries', initialWidth: 80 },
    { key: 'identifiers', label: 'Identifiers', initialWidth: 380 },
    { key: 'last_activity', label: 'Last Activity', initialWidth: 150 },
  ], [])

  if (selectedCampaignId) {
    return (
      <CampaignDetailPage
        campaignId={selectedCampaignId}
        theme={theme}
        storageKey={storageKey}
        isEditor={isEditor}
        onClose={() => setSelectedCampaignId(null)}
      />
    )
  }

  return (
    <BaseDashboard
      rootRef={back.restoreRef}
      theme={theme}
      eyebrow="Moderation · Campaigns"
      title="Campaigns"
      subtitle="Entries grouped by the domains/entity_references they share — computed fresh on every load, nothing here is curated or stored."
      infoContent={
        <>
          <p>A campaign is a connected component of the (entry ↔ identifier) graph: a domain or entity_reference is a campaign on its own, and identifiers that co-occur on the same entry merge into one.</p>
          <p className="mt-1.5"><span className="font-semibold">campaign_id is not a stable key</span> — it can change identity when a new entry bridges two previously-separate campaigns. Never bookmark it; this view always resolves it fresh.</p>
        </>
      }
      onRefresh={() => fetchCampaigns(0, true)}
      refreshing={refreshing}
      loading={loading}
      error={error}
      onRetry={() => fetchCampaigns(0, true)}
      storageKey={`campaigns-${storageKey}-header`}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-background px-4 py-3.5">
        <Segmented size="sm" value={onlyMulti ? 'multi' : 'all'} onChange={(v) => setOnlyMulti(v === 'multi')}
          segments={[{ value: 'all', label: 'All' }, { value: 'multi', label: 'Multi-identifier', title: "campaign_entry_count > 1 OR campaign_type LIKE 'multiple%' — the single-identifier campaigns (the overwhelming majority) are the uninteresting long tail" }]} />
        <select
          value={campaignType}
          onChange={(e) => setCampaignType(e.target.value)}
          className="h-8 rounded-[2px] border border-border bg-background px-2 font-mono text-label text-foreground"
        >
          {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className="w-48">
          <SearchableSelect theme={theme} value={platformFilter === '' ? undefined : platformFilter} options={catalog.platforms}
            placeholder="All platforms" searchPlaceholder="Search platforms…" noneLabel="All platforms"
            onChange={(id) => setPlatformFilter(id ? Number(id) : '')} />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'recent' | 'entries')}
          className="h-8 rounded-[2px] border border-border bg-background px-2 font-mono text-label text-foreground"
        >
          <option value="recent">Sort: Most recent activity</option>
          <option value="entries">Sort: Most entries</option>
        </select>
        <span className={`ml-auto font-mono text-label whitespace-nowrap ${muted}`}>
          {campaigns.length} / {total ?? campaigns.length}
        </span>
        {scanCapped && (
          <StatusPill tone="warning" title="The underlying scan hit its row cap — total/hasMore are a lower bound, not exact. See getCampaignsFlat's own comment.">scan capped</StatusPill>
        )}
      </div>
      <ResizableTable
        columns={columns}
        stickyHeader
        theme={theme}
        isEmpty={campaigns.length === 0}
        emptyMessage="No campaigns found"
      >
        {campaigns.map((c) => {
          const multi = isMultiIdentifierCampaign(c)
          return (
            <tr
              key={c.campaignId}
              data-last-opened={back.isLast(c.campaignId) || undefined}
              onClick={(ev) => back.open(c.campaignId, ev.currentTarget, () => setSelectedCampaignId(c.campaignId))}
              className={`cursor-pointer border-b border-border border-l-2 transition-colors hover:bg-secondary ${
                multi ? 'border-l-signal' : 'border-l-transparent'
              } ${back.isLast(c.campaignId) ? LAST_OPENED_ROW_CLASS : ''}`}
            >
              <TableTd className="font-mono">
                <StatusPill tone={multi ? 'signal' : 'neutral'} dot={false}>{c.campaignType ?? '—'}</StatusPill>
              </TableTd>
              <TableTd className={`font-mono ${muted}`}>{c.entryCount}</TableTd>
              <TableTd>
                <div className="flex flex-wrap items-center gap-1.5">
                  {identifierSummary(c).map((part, i) => (
                    <Tag key={i} className="gap-1">
                      {part.icon === 'domain' ? <Globe className="h-2.5 w-2.5" /> : <Hash className="h-2.5 w-2.5" />}
                      {part.text}
                    </Tag>
                  ))}
                </div>
              </TableTd>
              <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(c.lastCreatedAt)}</TableTd>
            </tr>
          )
        })}
      </ResizableTable>
      {hasMore && (
        <div className={`flex justify-center border-t px-4 py-3 ${border}`}>
          <Button variant="action" size="sm" onClick={() => fetchCampaigns(page + 1)} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load more'}</Button>
        </div>
      )}
    </BaseDashboard>
  )
}
